import type { BobState } from "../types";
import { computeDeckMetrics, STAGE_LABELS } from "../lib/engine/pipeline";

interface Props {
  state: BobState;
  onTogglePresentation: () => void;
  onNavigate: (surface: BobState["activeSurface"]) => void;
}

export function PortfolioModePanel({ state, onTogglePresentation, onNavigate }: Props) {
  const metrics = computeDeckMetrics(state);
  const compact = state.presentationMode;

  const byStage = state.directives.reduce<Record<string, number>>((acc, d) => {
    acc[d.stage] = (acc[d.stage] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <section className="surface glass-panel">
      <h2>{compact ? "Portfolio" : "Portfolio Overview"}</h2>
      <p className="lead">
        {compact
          ? "Executive build portfolio snapshot."
          : "Executive snapshot of all build directives — toggle portfolio mode in the header to condense copy across all surfaces."}
      </p>

      <div className="status-grid">
        <div className="status-card">
          <h3>{compact ? "Builds" : "Total builds"}</h3>
          <div className="score-ring" style={{ fontSize: "1.5rem" }}>
            {metrics.totalDirectives}
          </div>
        </div>
        <div className="status-card">
          <h3>{compact ? "Progress" : "Avg progress"}</h3>
          <div className="score-ring" style={{ fontSize: "1.5rem" }}>
            {metrics.avgProgress}%
          </div>
        </div>
        <div className="status-card">
          <h3>{compact ? "Risk" : "Open failures"}</h3>
          <div className="score-ring" style={{ fontSize: "1.5rem", color: metrics.openFailures ? "var(--accent-danger)" : "var(--accent-ok)" }}>
            {metrics.openFailures}
          </div>
        </div>
      </div>

      {state.directives.length > 0 && (
        <div className="portfolio-table">
          {state.directives.map((d) => (
            <div key={d.id} className="portfolio-row">
              <span className="portfolio-name">{d.name}</span>
              <span className="chip">{STAGE_LABELS[d.stage]}</span>
              <span className="chip">{d.progress}%</span>
              <span className={`chip ${d.paused ? "locked" : "ok"}`}>{d.paused ? "hold" : d.priority}</span>
            </div>
          ))}
        </div>
      )}

      {!compact && Object.keys(byStage).length > 0 && (
        <div className="output-box">
          Stage distribution:{"\n"}
          {Object.entries(byStage)
            .map(([stage, count]) => `${STAGE_LABELS[stage as keyof typeof STAGE_LABELS] ?? stage}: ${count}`)
            .join("\n")}
        </div>
      )}

      <div className="btn-row">
        <button type="button" className="btn gold" onClick={onTogglePresentation}>
          {state.presentationMode ? "Exit portfolio mode" : "Enable portfolio mode"}
        </button>
        <button type="button" className="btn secondary" onClick={() => onNavigate("deck")}>
          Command deck
        </button>
      </div>
    </section>
  );
}
